
import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { format } from 'date-fns';
import { PredictionChartData } from './types';

interface PredictionLineChartProps {
  metal: 'ORO' | 'ARGENTO';
  timeSeries: PredictionChartData['timeSeries'];
  currentPrice: number;
}

export function PredictionLineChart({ metal, timeSeries, currentPrice }: PredictionLineChartProps) {
  const metalColor = metal === 'ORO' ? '#FFD700' : '#C0C0C0';
  
  // Trova l'ultimo indice con dati di previsione usando un approccio compatibile
  const findLastPredictionIndex = () => {
    for (let i = timeSeries.length - 1; i >= 0; i--) {
      if (timeSeries[i].predictedHigh !== undefined || 
          timeSeries[i].predictedAverage !== undefined || 
          timeSeries[i].predictedLow !== undefined) {
        return i;
      }
    }
    return -1;
  };
  
  const lastPredictionIndex = findLastPredictionIndex();
  
  // Calcola il valore minimo dai dati per evitare valori negativi nell'asse Y
  const calculateMinValue = () => {
    const allValues = timeSeries.flatMap(item => [
      item.historical,
      item.predictedHigh,
      item.predictedAverage,
      item.predictedLow
    ]).filter(val => val !== undefined && val !== null);
    
    const minValue = Math.min(...allValues);
    // Assicurati che il minimo non sia mai negativo
    return Math.max(0, minValue * 0.95); // 5% di padding sotto il valore minimo
  };
  
  // Funzione per renderizzare le label personalizzate
  const renderCustomLabel = (props: any) => {
    const { x, y, index, dataKey, value } = props;
    
    // Mostra la label solo sull'ultimo punto con dati di previsione
    if (index !== lastPredictionIndex || !value) return null;
    
    // Determina il colore in base al tipo di previsione
    let color = '#666';
    let bgColor = '#fff';
    
    if (dataKey === 'predictedHigh') {
      color = '#22c55e';
      bgColor = '#dcfce7';
    } else if (dataKey === 'predictedAverage') {
      color = '#eab308';
      bgColor = '#fef3c7';
    } else if (dataKey === 'predictedLow') {
      color = '#ef4444';
      bgColor = '#fee2e2';
    }
    
    return (
      <g>
        <rect
          x={x + 8}
          y={y - 12}
          width={50}
          height={20}
          fill={bgColor}
          stroke={color}
          strokeWidth={1}
          rx={4}
        />
        <text
          x={x + 33}
          y={y - 2}
          textAnchor="middle"
          fontSize={11}
          fontWeight="600"
          fill={color}
        >
          €{value.toFixed(2)}
        </text>
      </g>
    );
  };
  
  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart
        data={timeSeries}
        margin={{ top: 20, right: 80, left: 20, bottom: 20 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis 
          dataKey="date" 
          tickFormatter={(value) => format(new Date(value), 'dd/MM')}
          stroke="#6b7280"
          fontSize={12}
        />
        <YAxis 
          stroke="#6b7280"
          fontSize={12}
          tickFormatter={(value) => `€${value.toFixed(2)}`}
          domain={[calculateMinValue, 'dataMax']}
        />
        <Tooltip 
          formatter={(value: number, name: string) => [
            `€${value.toFixed(2)}`, 
            name === 'historical' ? 'Storico' : 
            name === 'predictedHigh' ? 'Scenario Alto' :
            name === 'predictedAverage' ? 'Scenario Medio' : 
            name === 'predictedLow' ? 'Scenario Basso' : name
          ]}
          labelFormatter={(value) => format(new Date(value), 'dd/MM/yyyy')}
          contentStyle={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}
        />
        <Legend />
        
        {/* Linea storica */}
        <Line
          type="monotone"
          dataKey="historical"
          stroke={metalColor}
          strokeWidth={2}
          dot={false}
          name="Dati storici"
          connectNulls={false}
        />
        
        {/* Linea previsione alta */}
        <Line
          type="monotone"
          dataKey="predictedHigh"
          stroke="#22c55e"
          strokeWidth={2}
          strokeDasharray="5 5"
          dot={false}
          name="Scenario Alto"
          connectNulls={false}
        >
          <LabelList content={renderCustomLabel} />
        </Line>
        
        {/* Linea previsione media */}
        <Line
          type="monotone"
          dataKey="predictedAverage"
          stroke="#eab308"
          strokeWidth={2}
          strokeDasharray="5 5"
          dot={false}
          name="Scenario Medio"
          connectNulls={false}
        >
          <LabelList content={renderCustomLabel} />
        </Line>
        
        {/* Linea previsione bassa */}
        <Line
          type="monotone"
          dataKey="predictedLow"
          stroke="#ef4444"
          strokeWidth={2}
          strokeDasharray="5 5"
          dot={false}
          name="Scenario Basso"
          connectNulls={false}
        >
          <LabelList content={renderCustomLabel} />
        </Line>
      </LineChart>
    </ResponsiveContainer>
  );
}
